import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import type { MainStopFormFields, RouteStopSaveRow } from './routeStopsSavePipeline';

export type SequentialSaveBuildParams = {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
};

type StopContext = {
  stop: DeliveryGroupStopDraft;
  group: DeliveryGroupDraft;
};

function collectStopContexts(groups: DeliveryGroupDraft[]): StopContext[] {
  const byUid = new Map<string, StopContext>();
  const sortedGroups = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const group of sortedGroups) {
    for (const stop of [...group.loadingStops, ...group.unloadingStops]) {
      if (!byUid.has(stop.uid)) {
        byUid.set(stop.uid, { stop, group });
      }
    }
  }
  return Array.from(byUid.values());
}

function hasEstablishedItinerary(contexts: StopContext[]): boolean {
  if (contexts.length === 0) return false;
  return contexts.every(
    (c) =>
      c.stop.position != null &&
      Number.isFinite(Number(c.stop.position)) &&
      Number(c.stop.position) >= 0
  );
}

function buildIdToUidMap(contexts: StopContext[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const { stop } of contexts) {
    if (stop.id != null) map.set(Number(stop.id), stop.uid);
  }
  return map;
}

function resolveParentLoadingStopUid(params: {
  stop: DeliveryGroupStopDraft;
  group: DeliveryGroupDraft;
  mainLoadingUid: string;
  idToUid: Map<number, string>;
}): string {
  if (params.stop.parentLoadingStopId != null) {
    const fromId = params.idToUid.get(Number(params.stop.parentLoadingStopId));
    if (fromId) return fromId;
  }
  const groupLoadingUid = params.group.loadingStops[0]?.uid;
  if (groupLoadingUid) return groupLoadingUid;
  return params.mainLoadingUid;
}

function overlayMainFields(
  row: RouteStopSaveRow,
  fields: MainStopFormFields,
  primaryDraft?: DeliveryGroupStopDraft | null
): void {
  if (fields.id != null && row.id == null) row.id = fields.id;
  row.address = fields.address ?? primaryDraft?.address ?? row.address ?? null;
  row.city = fields.city ?? primaryDraft?.city ?? row.city ?? null;
  row.contactPerson = fields.contactPerson ?? primaryDraft?.contactPerson ?? row.contactPerson ?? null;
  row.phone = fields.phone ?? primaryDraft?.phone ?? row.phone ?? null;
  row.plannedAt = fields.plannedAt ?? primaryDraft?.plannedAt ?? row.plannedAt ?? null;
  row.actualAt = fields.actualAt ?? primaryDraft?.actualAt ?? row.actualAt ?? null;
  row.pointType = fields.pointType ?? primaryDraft?.pointType ?? row.pointType ?? null;
  row.pointId = fields.pointId ?? primaryDraft?.pointId ?? row.pointId ?? null;
  row.status = fields.status ?? primaryDraft?.status ?? row.status ?? null;
}

function stopContextToRow(
  ctx: StopContext,
  parentLoadingStopUid: string | undefined
): RouteStopSaveRow & { _parentLoadingStopUid?: string } {
  const { stop, group } = ctx;
  return {
    id: stop.id ?? undefined,
    type: stop.type,
    position: -1,
    pointType: stop.pointType ?? null,
    pointId: stop.pointId ?? null,
    address: stop.address ?? null,
    city: stop.city ?? null,
    contactPerson: stop.contactPerson ?? null,
    phone: stop.phone ?? null,
    plannedAt: stop.plannedAt ?? null,
    actualAt: stop.actualAt ?? null,
    notes: stop.notes ?? null,
    status: stop.status ?? null,
    parentLoadingPosition: null,
    parentLoadingStopId: stop.parentLoadingStopId ?? null,
    _stopUid: stop.uid,
    _groupUid: group.uid,
    ...(stop.type === 'unloading' && parentLoadingStopUid
      ? { _parentLoadingStopUid: parentLoadingStopUid }
      : {})
  };
}

/**
 * Preserves global itinerary order from draft.position (UID-first; position is execution order only).
 * Does NOT batch loadings before unloadings.
 */
export function buildSequentialRouteStopsSavePayload(
  params: SequentialSaveBuildParams
): RouteStopSaveRow[] {
  const contexts = collectStopContexts(params.groups);
  if (!hasEstablishedItinerary(contexts)) {
    throw new Error('SEQUENTIAL_ITINERARY_POSITIONS_REQUIRED');
  }

  const sorted = [...contexts].sort(
    (a, b) =>
      Number(a.stop.position ?? 0) - Number(b.stop.position ?? 0) ||
      a.stop.uid.localeCompare(b.stop.uid)
  );

  const sortedGroups = [...params.groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const primary = sortedGroups.find((g) => g.isDefault) ?? sortedGroups[0];
  const mainLoadingUid = primary?.loadingStops[0]?.uid ?? params.mainLoading.uid ?? 'main-loading-0';
  const mainUnloadingUid = primary?.unloadingStops[0]?.uid ?? params.mainUnloading.uid ?? 'main-unloading';
  const idToUid = buildIdToUidMap(contexts);

  const rows: Array<RouteStopSaveRow & { _parentLoadingStopUid?: string }> = [];

  for (const ctx of sorted) {
    const parentUid =
      ctx.stop.type === 'unloading'
        ? resolveParentLoadingStopUid({
            stop: ctx.stop,
            group: ctx.group,
            mainLoadingUid,
            idToUid
          })
        : undefined;
    rows.push(stopContextToRow(ctx, parentUid));
  }

  const positionByUid = new Map<string, number>();
  for (let index = 0; index < rows.length; index++) {
    rows[index].position = index;
    const uid = rows[index]._stopUid;
    if (uid) positionByUid.set(uid, index);
  }

  for (const row of rows) {
    if (row.type !== 'unloading') continue;
    const parentUid = row._parentLoadingStopUid;
    row.parentLoadingPosition =
      parentUid && positionByUid.has(parentUid) ? positionByUid.get(parentUid)! : 0;
  }

  for (const row of rows) {
    if (row._stopUid === mainLoadingUid) {
      overlayMainFields(row, params.mainLoading, primary?.loadingStops[0]);
    }
    if (row._stopUid === mainUnloadingUid) {
      overlayMainFields(row, params.mainUnloading, primary?.unloadingStops[0]);
    }
  }

  return rows;
}

export function canBuildSequentialRouteStopsSavePayload(groups: DeliveryGroupDraft[]): boolean {
  return hasEstablishedItinerary(collectStopContexts(groups));
}
